import { useEffect, useState } from 'react'
import { endpoints, type BoardMember } from '../../api/client'
import { Avatar, ramp } from '../../components/Avatar'
import { Skeleton, WidgetHeader } from './Widgets'

/*
  Row three: proof of work, and the board's own voice.

  Everything this product does between Fridays happens in a job queue nobody can see.
  Items get clarified, anomalies get flagged, prep gets written. Until now none of it left
  a mark on any screen, so a quiet week looked like an idle product. These two cards are
  where that shows up.

  Both collapse when they have nothing, rather than sitting there as a pair of empty
  boxes. A new account should see five widgets and no scaffolding.
*/

interface ActivityEntry {
  kind: 'captured' | 'asked' | 'flagged' | 'closed'
  text: string
  at: string
  memberId: string | null
}

const KIND: Record<ActivityEntry['kind'], { icon: string; label: string }> = {
  captured: { icon: '+', label: 'on the table' },
  asked:    { icon: '?', label: 'asked' },
  flagged:  { icon: '!', label: 'flagged' },
  closed:   { icon: '✓', label: 'closed out' },
}

export function ThisWeek({ board }: { board: BoardMember[] }) {
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null)

  useEffect(() => {
    void endpoints.activity().then(setEntries).catch(() => setEntries([]))
  }, [])

  if (entries !== null && entries.length === 0) return null

  return (
    <section className="w w--week">
      <WidgetHeader title="This week" />

      {entries === null ? (
        <Skeleton rows={3} />
      ) : (
        <ul className="timeline">
          {entries.map((e, i) => {
            const member = board.find(m => m.id === e.memberId) ?? null
            return (
              <li key={`${e.at}-${i}`} className="tl" style={member ? ramp(member.colorRamp) : undefined}>
                <span className={`tl__icon tl__icon--${e.kind}`} aria-hidden>{KIND[e.kind].icon}</span>
                <span className="tl__when">{when(e.at)}</span>
                <span className="tl__text">{e.text.replace(/\s+/g, ' ').trim()}</span>
                <span className="tl__who">
                  {member ? <Avatar member={member} size="sm" /> : <span className="tl__label">{KIND[e.kind].label}</span>}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

interface Seasonal {
  prompt?: string
  memberName?: string | null
  memberFace?: string | null
  seasonal?: boolean
}

export function SeasonalCard({
  board, onCaptured,
}: {
  board: BoardMember[]
  onCaptured: (text: string) => Promise<void>
}) {
  const [card, setCard] = useState<Seasonal | null>(null)
  const [added, setAdded] = useState(false)

  useEffect(() => {
    void endpoints.seasonal().then(setCard).catch(() => setCard({}))
  }, [])

  if (card !== null && !card.prompt) return null

  const member = board.find(m => m.displayName === card?.memberName) ?? null

  return (
    <section className="w w--seasonal" style={member ? ramp(member.colorRamp) : undefined}>
      <WidgetHeader title={card?.seasonal ? 'This time of year' : 'Standing question'} />

      {card === null ? (
        <Skeleton rows={3} />
      ) : (
        <>
          <p className="seasonal__prompt">{card.prompt}</p>

          <div className="seasonal__foot">
            {member && <Avatar member={member} size="sm" />}
            <span className="dim">{card.memberName ?? 'The board'}</span>
          </div>

          <button
            className="btn btn--ghost"
            disabled={added}
            onClick={async () => { await onCaptured(card.prompt!); setAdded(true) }}
          >
            {added ? 'On the table' : 'Add to Friday'}
          </button>
        </>
      )}
    </section>
  )
}

function when(iso: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''

  const hours = Math.round((Date.now() - then.getTime()) / 3_600_000)
  if (hours < 1) return 'just now'
  if (hours < 24) return `${hours}h ago`

  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`

  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}
